import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, 
  Upload, 
  Loader2, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Calendar,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientMonthlySubmissionsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [uploadType, setUploadType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  
  const currentDate = new Date();
  const [newSubmission, setNewSubmission] = useState({
    period_month: currentDate.getMonth() + 1,
    period_year: currentDate.getFullYear()
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSubmissions();
  }, [token]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/client/monthly-submissions`, { headers });
      setSubmissions(response.data || []);
    } catch (err) {
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubmission = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      const response = await axios.post(
        `${API_URL}/api/client/monthly-submissions?period_month=${newSubmission.period_month}&period_year=${newSubmission.period_year}`,
        {},
        { headers }
      );
      toast.success(response.data.message);
      setCreateOpen(false);
      fetchSubmissions();
    } catch (err) {
      console.error('Error creating submission:', err);
      toast.error(err.response?.data?.detail || 'Failed to create submission');
    } finally {
      setCreating(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !selectedSubmission || !uploadType) {
      toast.error('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      setUploading(true);
      const response = await axios.post(
        `${API_URL}/api/client/monthly-submissions/${selectedSubmission.id}/upload?document_type=${uploadType}`,
        formData,
        { 
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success(response.data.message);
      setUploadOpen(false);
      setSelectedFile(null);
      fetchSubmissions();
    } catch (err) {
      console.error('Error uploading:', err);
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const openUploadDialog = (submission, type) => {
    setSelectedSubmission(submission);
    setUploadType(type);
    setSelectedFile(null);
    setUploadOpen(true);
  };

  const getMonthName = (month) => {
    return new Date(2024, month - 1).toLocaleString('en-ZA', { month: 'long' });
  };

  const getStatusBadge = (status) => {
    const variants = {
      complete: 'bg-green-100 text-green-800',
      partial: 'bg-amber-100 text-amber-800',
      pending: 'bg-gray-100 text-gray-600',
      reviewed: 'bg-blue-100 text-blue-800'
    };
    return variants[status] || 'bg-gray-100 text-gray-600';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'partial': return <AlertCircle className="h-5 w-5 text-amber-500" />;
      case 'reviewed': return <CheckCircle className="h-5 w-5 text-blue-500" />;
      default: return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  // Generate month options for last 12 months
  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    monthOptions.push({
      month: date.getMonth() + 1,
      year: date.getFullYear(),
      label: `${getMonthName(date.getMonth() + 1)} ${date.getFullYear()}`
    });
  }

  return (
    <div className="space-y-6" data-testid="client-monthly-submissions-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#212529] font-['Manrope']">Monthly Document Submissions</h1>
          <p className="text-[#6C757D]">Submit your management accounts and bank statements</p>
        </div>
        <Button
          className="bg-[#0D3B66] hover:bg-[#0A2F52]"
          onClick={() => setCreateOpen(true)}
          data-testid="new-submission-btn"
        >
          <Calendar className="h-4 w-4 mr-2" />
          New Submission
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Building2 className="h-6 w-6 text-blue-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-blue-900">Monthly Requirements</p>
              <p className="text-sm text-blue-700 mt-1">
                Please submit your management accounts and bank statements by the 15th of each month for the previous month. 
                These documents are required for ongoing compliance and credit review.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Submissions List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#0D3B66]" />
        </div>
      ) : submissions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-[#6C757D] mb-4">No submissions yet</p>
            <Button onClick={() => setCreateOpen(true)}>
              Create Your First Submission
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {submissions.map((submission) => (
            <Card key={submission.id}>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    {getStatusIcon(submission.status)}
                    <div>
                      <p className="font-bold text-lg text-[#212529]">
                        {getMonthName(submission.period_month)} {submission.period_year}
                      </p>
                      <Badge className={getStatusBadge(submission.status)}>
                        {submission.status}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="flex flex-col md:flex-row gap-3">
                    {/* Management Accounts */}
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg min-w-[200px]">
                      <div className="flex-1">
                        <p className="text-sm font-medium">Management Accounts</p>
                        <p className={`text-xs ${submission.management_accounts_uploaded ? 'text-green-600' : 'text-gray-400'}`}>
                          {submission.management_accounts_uploaded ? 'Uploaded' : 'Not uploaded'}
                        </p>
                      </div>
                      {!submission.management_accounts_uploaded && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openUploadDialog(submission, 'management_accounts')}
                        >
                          <Upload className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    {/* Bank Statements */}
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg min-w-[200px]">
                      <div className="flex-1">
                        <p className="text-sm font-medium">Bank Statements</p>
                        <p className={`text-xs ${submission.bank_statements_uploaded ? 'text-green-600' : 'text-gray-400'}`}>
                          {submission.bank_statements_uploaded ? 'Uploaded' : 'Not uploaded'}
                        </p>
                      </div>
                      {!submission.bank_statements_uploaded && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openUploadDialog(submission, 'bank_statements')}
                        >
                          <Upload className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    {/* Annual Report */}
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg min-w-[200px]">
                      <div className="flex-1">
                        <p className="text-sm font-medium">Annual Report</p>
                        <p className={`text-xs ${submission.annual_report_uploaded ? 'text-green-600' : 'text-gray-400'}`}>
                          {submission.annual_report_uploaded ? 'Uploaded' : 'Not uploaded'}
                        </p>
                      </div>
                      {!submission.annual_report_uploaded && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openUploadDialog(submission, 'annual_report')}
                          data-testid={`upload-annual-report-${submission.id}`}
                        >
                          <Upload className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Submission Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Monthly Submission</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateSubmission} className="space-y-4">
            <div>
              <Label>Select Period</Label>
              <Select 
                value={`${newSubmission.period_month}-${newSubmission.period_year}`}
                onValueChange={(v) => {
                  const [month, year] = v.split('-');
                  setNewSubmission({ period_month: parseInt(month), period_year: parseInt(year) });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(opt => (
                    <SelectItem key={`${opt.month}-${opt.year}`} value={`${opt.month}-${opt.year}`}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating} className="bg-[#0D3B66] hover:bg-[#0A2F52]">
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Submission
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Upload {uploadType === 'management_accounts' ? 'Management Accounts' : uploadType === 'annual_report' ? 'Annual Report' : 'Bank Statements'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <Label>Select File</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="mt-2"
              />
              <p className="text-xs text-[#6C757D] mt-1">
                Accepted formats: PDF, JPG, PNG, XLS, XLSX
              </p>
            </div>
            {selectedFile && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium">{selectedFile.name}</p>
                <p className="text-xs text-[#6C757D]">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setUploadOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading || !selectedFile} className="bg-[#0D3B66] hover:bg-[#0A2F52]">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientMonthlySubmissionsPage;
